/** The generic command bus every state domain shares: `Batch`, the leaf-reducer contract, and `applyWithReducer`. */

import { ok, type Result } from "./result";

/**
 * Apply a list of commands as a single atomic unit. Applies in order; its
 * inverse is the reversed list of each child's inverse, so a batch ("deal burn
 * to all PCs") undoes as one unit. Batches may nest.
 *
 * The batch shape is **generic over the command union** so the same atomic
 * machinery drives any state domain: a `Batch<RosterCommand>` carries roster
 * commands. The presence `Command` union below ties the knot to its own variants.
 *
 * Must stay an `interface`: `Batch<Cmd>` is the generic that closes recursive
 * command-union knots across domains (`Command = … | Batch<Command>`,
 * `EncounterCommand = … | Batch<EncounterCommand>`). Interfaces resolve lazily,
 * so the self-reference is legal; a `type` alias resolves eagerly and TS rejects
 * it as "circularly references itself" (TS2456). This is a sanctioned exception
 * to the type-not-interface rule.
 */
// oxlint-disable-next-line typescript/consistent-type-definitions
export interface Batch<Cmd> {
  type: "batch";
  commands: Cmd[];
}

/** A command's reducer was given state it can't act on (e.g. unknown actor). */
export class CommandError extends Error {}

// ── Generic command-bus core ─────────────────────────────────────────────────

/**
 * Read-only context threaded into every reducer call. It carries the
 * authoritative reference data a rule may need to validate a command —
 * principally the **compendium**, injected at the call site like a db handle and
 * NEVER sent on the wire. The reducer treats it as read-only; determinism holds
 * because every participant loads the same pack set (negotiated once on join via
 * the `welcome` handshake — see `WelcomeMessage.packs`).
 *
 * The field is optional so raw-only rules (and the presence domain, which needs
 * no reference data) can ignore it entirely; reference-dependent reducers read
 * `ctx.compendium` and reject when content is missing.
 */
export type ApplyCtx = {
  /**
   * The local compendium handle, opaque to this package (typed by the consuming
   * domain reducer). `undefined` when no reference data is wired — a
   * reference-dependent rule should then reject as missing-content.
   */
  compendium?: unknown;
}

/**
 * The result of applying a command: the new state, the command that undoes it
 * (its inverse), and the events to broadcast. Generic over the state, command,
 * and event types so the same result shape serves every domain. Pushing
 * `inverse` onto an undo stack is what makes undo/redo generic.
 */
export type ApplyResult<State, Cmd, Event> = {
  state: State;
  inverse: Cmd;
  events: Event[];
}

/**
 * A domain's **leaf reducer**: the per-domain logic that applies one
 * *non-batch* command. The generic `applyWithReducer` owns the `batch`
 * machinery (atomic apply + reversed-inverse + event concat) and delegates
 * every leaf command here. Pure and deterministic — same `(state, command,
 * ctx)` → same result — and it builds each command's inverse where the "before"
 * is in scope.
 *
 * It **returns a {@link Result}**: `ok(applyResult)` on success,
 * `err(new CommandError(...))` for an *expected* rejection (unknown actor, illegal
 * move, an unknown leaf type). It does **not** `throw` for an expected rejection —
 * throw is narrowed to genuine programmer errors (a real bug), which still
 * propagate. The bus short-circuits on the first `err` (a batch aborts before any
 * state commits), so the failure path is type-enforced end to end.
 */
export type LeafReducer<State, Cmd, Event> = (
  state: State,
  command: Exclude<Cmd, Batch<Cmd>>,
  ctx: ApplyCtx,
) => Result<ApplyResult<State, Cmd, Event>, CommandError>;

/** Structural batch guard — a command is a batch if it carries child commands. */
function isBatch<Cmd>(command: Cmd): command is Cmd & Batch<Cmd> {
  return (
    typeof command === "object" &&
    command !== null &&
    (command as { type?: unknown }).type === "batch"
  );
}

/**
 * The generic command bus. Given a domain's leaf reducer, applies any command
 * (leaf or `batch`) to that domain's state, returning the new state, the
 * inverse, and the events. This is the **one piece of machinery shared across
 * state domains** (presence `PresenceState`, roster `RosterState`, …): the
 * `batch` recursion lives here once, so every domain gets atomic batches and
 * generic undo for free, and each domain only writes its leaf cases.
 *
 * A `batch` applies atomically — because reducers are pure, a child rejecting
 * aborts the whole batch before any caller commits state — and inverts
 * atomically (the reversed list of child inverses).
 *
 * Returns a {@link Result}: `ok(applyResult)` on success, `err(commandError)` on an
 * *expected* reducer rejection. The leaf reducer *returns* `err(...)` for an expected
 * rejection rather than throwing, and the `batch` path is a **short-circuit fold over
 * `Result`**: it threads each child's new state forward and returns the first child's
 * `err` immediately — so a failing child aborts the whole batch **before any state is
 * committed** (atomicity is structural, not a throw-unwind). A genuine bug (a real
 * `throw`, not a `Result`) still propagates unmodified — there is no `try/catch`
 * bridge here.
 */
/** Named arguments for {@link applyWithReducer}. */
export type ApplyWithReducerArgs<State, Cmd extends { type: string }, Event> = {
  reducer: LeafReducer<State, Cmd, Event>;
  state: State;
  command: Cmd;
  ctx?: ApplyCtx;
}

export function applyWithReducer<State, Cmd extends { type: string }, Event>(
  args: ApplyWithReducerArgs<State, Cmd, Event>,
): Result<ApplyResult<State, Cmd, Event>, CommandError> {
  const { reducer, state, command, ctx = {} } = args;
  if (isBatch(command)) {
    let cur = state;
    const inverses: Cmd[] = [];
    const events: Event[] = [];
    for (const child of command.commands) {
      const res = applyWithReducer({ reducer, state: cur, command: child, ctx });
      // Short-circuit: the first failing child aborts the batch and returns its
      // `err` — `cur` (the accumulated state) is discarded, never committed, so
      // the batch is atomic (no partial commit).
      if (!res.ok) return res;
      cur = res.value.state;
      // Prepend so the inverse list reverses child order: undo the
      // last-applied child first.
      inverses.unshift(res.value.inverse);
      events.push(...res.value.events);
    }
    const inverse = { type: "batch", commands: inverses } as Cmd & Batch<Cmd>;
    return ok({ state: cur, inverse, events });
  }
  // Not a batch — a leaf the domain reducer owns. It returns its own `Result`.
  return reducer(state, command as Exclude<Cmd, Batch<Cmd>>, ctx);
}